import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Aperture, Skull, Camera, BookImage } from "lucide-react";
import { createSession, clearStoredSessionId } from "../game/api";
import AccessibilityBar from "../components/game/AccessibilityBar";

const STORY_LINES = [
    "Friday. 11:47 PM. You — the underpaid yearbook photographer — were just locked",
    "in the basement darkroom. Five rooms stand between you and the exit.",
    "Five impossible photographs. And he is here. Watching the prints develop.",
    "Master the Exposure Triangle. Or join the wall of failed negatives.",
];

export default function IntroScreen({
    ttsEnabled, setTTSEnabled,
    hcMode, setHCMode,
    audioEnabled, setAudioEnabled,
    practiceMode, setPracticeMode,
}) {
    const nav = useNavigate();
    const [nickname, setNickname] = useState("Photographer");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const begin = async () => {
        setLoading(true);
        setErr(null);
        try {
            clearStoredSessionId();
            await createSession(nickname || "Photographer");
            nav("/play");
        } catch (e) {
            setErr("Could not connect to the darkroom. Check the wires.");
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative px-4 sm:px-8 lg:px-16 py-8 flex flex-col" data-testid="intro-screen">
            <header className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Aperture size={22} className="text-amber" />
                    <span className="text-[10px] tracking-[0.35em] uppercase text-[var(--text-secondary)]">
                        Roll · 35mm · Exp. 24/36
                    </span>
                </div>
                <AccessibilityBar
                    ttsEnabled={ttsEnabled} setTTSEnabled={setTTSEnabled}
                    hcMode={hcMode} setHCMode={setHCMode}
                    audioEnabled={audioEnabled} setAudioEnabled={setAudioEnabled}
                    practiceMode={practiceMode} setPracticeMode={setPracticeMode}
                />
            </header>

            <main className="flex-1 flex flex-col lg:flex-row gap-10 lg:gap-16 items-start lg:items-center mt-10 lg:mt-0">
                <section className="lg:w-3/5">
                    <div className="text-[10px] tracking-[0.4em] uppercase text-amber mb-3 safelight">
                        ▌ Darkroom Sealed · Phantom Active
                    </div>
                    <h1 className="font-display uppercase tracking-tighter font-bold text-shadow-glow text-amber leading-[0.95] text-[clamp(2.6rem,7vw,5.5rem)]">
                        Exposure<br/>in the Dark
                    </h1>
                    <p className="text-[var(--text-secondary)] mt-3 italic text-sm tracking-wide">
                        The Phantom of the Darkroom — A photographic survival lesson
                    </p>

                    <div className="mt-8 max-w-xl font-mono text-sm leading-relaxed text-[var(--text-primary)] border-l-2 border-amber-30 pl-4">
                        {STORY_LINES.map((l) => <p key={l} className="mb-1">{l}</p>)}
                    </div>

                    <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
                        <Stat icon={<Camera size={14} />} label="Rooms" value="5" />
                        <Stat icon={<Aperture size={14} />} label="Exposure Triangle" value="I · S · A" />
                        <Stat icon={<Skull size={14} />} label="Phantom" value="1" />
                    </div>
                </section>

                <section className="lg:w-2/5 w-full max-w-md" data-testid="intro-form">
                    <div className="border border-amber-30 bg-bg2 p-6 relative scanlines">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-amber mb-4 flex items-center gap-2">
                            <BookImage size={12} /> Sign the Roll
                        </div>
                        <label className="text-[10px] tracking-[0.25em] uppercase text-[var(--text-secondary)] block mb-2">
                            Photographer Tag
                        </label>
                        <input
                            type="text"
                            value={nickname}
                            onChange={(e) => setNickname(e.target.value.slice(0, 24))}
                            className="w-full bg-black border border-amber-30 px-3 py-2 text-amber font-mono uppercase tracking-widest text-sm focus:outline-none focus:border-amber"
                            placeholder="ANONYMOUS"
                            maxLength={24}
                            data-testid="nickname-input"
                            aria-label="Photographer nickname"
                        />
                        <button
                            onClick={begin}
                            disabled={loading}
                            className="mt-6 w-full bg-amber text-black font-display uppercase tracking-[0.25em] py-3 hover:bg-[var(--amber-glow)] active:translate-y-px transition disabled:opacity-50"
                            data-testid="enter-darkroom-btn"
                        >
                            {loading ? "Locking the door..." : "▶ Enter the Darkroom"}
                        </button>
                        {err && <div className="mt-3 text-xs text-phantom" data-testid="intro-error">{err}</div>}
                        <p className="mt-5 text-[10px] text-[var(--text-muted)] tracking-widest uppercase">
                            Progress is saved anonymously. No login. No mercy.
                        </p>
                    </div>
                </section>
            </main>

            <footer className="mt-12 flex justify-between text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">
                <span>© Kid Colston · Final Game Project</span>
                <span>v1.0 · Phantom Cut</span>
            </footer>
        </div>
    );
}

const Stat = ({ icon, label, value }) => (
    <div className="border border-amber-30 px-3 py-2.5 bg-black/40">
        <div className="flex items-center gap-1.5 text-amber text-[10px] tracking-widest uppercase">
            {icon}{label}
        </div>
        <div className="font-display text-xl mt-1 text-[var(--text-primary)]">{value}</div>
    </div>
);
